import { useEffect, useRef } from "react";

/**
 * 화면이 보이는 동안에만 주기적으로 다시 불러온다.
 *
 * 현장 정보처럼 **지금 맞는지가 값의 전부인** 데이터에 쓴다.
 * 줄 서서 화면을 보고 있는 사람에게 5분 전 정보를 계속 보여주면 안 된다.
 *
 * 숨어 있을 때는 멈춘다 — 보이지도 않는 화면 때문에 배터리와 요청을 쓸 이유가 없다.
 * 다시 보이는 순간에는 기다리지 않고 바로 한 번 불러온다. 그 순간이 가장 궁금한 때다.
 */
export function useVisibleRefresh(seconds: number, reload: () => void): void {
  const fn = useRef(reload);
  fn.current = reload;

  useEffect(() => {
    if (seconds <= 0) return;
    let timer: ReturnType<typeof setInterval> | null = null;

    const start = () => {
      if (timer !== null) return;
      timer = setInterval(() => fn.current(), seconds * 1000);
    };
    const stop = () => {
      if (timer === null) return;
      clearInterval(timer);
      timer = null;
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") { fn.current(); start(); }
      else stop();
    };

    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => { stop(); document.removeEventListener("visibilitychange", onVisibility); };
  }, [seconds]);
}
